/**
 * Templates (route /templates) — round9/design-pass, Direction A: promoted
 * from a card at the foot of the Rate Card page to its own top-level
 * destination. The list, inline rename and delete actions moved here
 * verbatim from SettingsPage.tsx (Round 4 D1 list/delete;
 * round8/template-authoring rename + editor links); authoring itself stays
 * on TemplateEditorPage (/templates/new, /templates/:id/edit), which
 * navigates back here with the saved name in route state.
 *
 * Independent of any Save button: list, rename and delete are separate,
 * immediate actions against their own endpoints (this repo's other list
 * actions, e.g. VersionHistoryPanel's restore, aren't gated behind a page
 * Save either). GET is open to owner/rep; every write is owner-only
 * server-side, and the page hides the write affordances from a `rep`
 * outright (round7/debt-sweep) while keeping the 403 → friendly-message
 * mapping as a defensive fallback in case the claim and the backend ever
 * drift.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import { templateWriteErrorMessage } from "../lib/templateErrors";
import {
  ApiError, getTenant, listTemplates, deleteTemplate, updateTemplate,
  type TenantTemplate, type TenantConfig,
} from "../lib/api";
import { templateConflicts, hasConflicts, describeConflicts } from "../lib/templateConflicts";
import { StatusMessage } from "../components/StatusMessage";
import ui from "../styles/ui.module.css";
import styles from "./TemplatesPage.module.css";

export function TemplatesPage() {
  const { user, signOut } = useAuth();

  const [templates, setTemplates] = useState<TenantTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  // round10/debt-sweep (DEBT round9/embed-config #2): the tenant's designer
  // configuration, read so each template can be checked against what
  // homeowners may actually choose. Best-effort: a failed read means no
  // warnings, never a broken Templates page.
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  // Inline rename. PUT /api/portal/templates/{id} accepts a name-only patch
  // precisely so a rename doesn't have to round-trip the whole design
  // through the editor. `renamingId` is the row currently in edit-in-place
  // mode (null = none); `renameDraft` is its pending value.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // A template just created by TemplateEditorPage, which navigates back here
  // with its name in route state so the confirmation lands next to the list
  // it changed rather than on a page the builder already left.
  const location = useLocation();
  const createdTemplateName =
    (location.state as { templateSaved?: string } | null)?.templateSaved ?? null;

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    listTemplates(user.idToken)
      .then((result) => {
        if (cancelled) return;
        setTemplates(result);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        setTemplatesError(err instanceof Error ? err.message : "Failed to load templates");
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, signOut]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getTenant(user.idToken)
      .then((result) => { if (!cancelled) setTenant(result); })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          signOut();
          return;
        }
        // Deliberately silent otherwise — see the state's comment.
      });
    return () => { cancelled = true; };
  }, [user, signOut]);

  // Template id -> one sentence naming what it uses that homeowners can't
  // choose ("Uses Roofs; Decking: Composite (ThruFlow)"). Empty until the
  // tenant has loaded; empty for every template when nothing conflicts.
  const conflicts = useMemo(() => {
    const map = new Map<string, string>();
    if (!tenant) return map;
    for (const t of templates) {
      const c = templateConflicts(t.designState, tenant);
      if (hasConflicts(c)) map.set(t.id, describeConflicts(c));
    }
    return map;
  }, [templates, tenant]);

  async function handleDeleteTemplate(templateId: string) {
    if (!user || deletingTemplateId) return;
    setTemplatesError(null);
    setDeletingTemplateId(templateId);
    try {
      await deleteTemplate(user.idToken, templateId);
      setTemplates(ts => ts.filter(t => t.id !== templateId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      // A 404 means it's already gone (e.g. deleted from another tab) —
      // treat that as success rather than a hard failure.
      if (err instanceof ApiError && err.status === 404) {
        setTemplates(ts => ts.filter(t => t.id !== templateId));
        return;
      }
      setTemplatesError(deleteTemplateErrorMessage(err));
    } finally {
      setDeletingTemplateId(null);
    }
  }

  // Commit an inline rename (name-only PUT). Trusts the server's returned
  // entry over the local draft, same posture as TemplateEditorPage: the
  // write path re-derives a canonical designState, so the response is
  // authoritative.
  async function handleRenameSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user || !renamingId || isRenaming) return;
    const trimmed = renameDraft.trim();
    if (!trimmed) {
      setTemplatesError("Enter a name for the template.");
      return;
    }
    // No-op rename: close the editor rather than spend a write (and rather
    // than risk a NO_FIELDS_TO_UPDATE if the name is all that could change).
    const current = templates.find(t => t.id === renamingId);
    if (current && current.name === trimmed) {
      setRenamingId(null);
      return;
    }
    setTemplatesError(null);
    setIsRenaming(true);
    try {
      const saved = await updateTemplate(user.idToken, renamingId, { name: trimmed });
      setTemplates(ts => ts.map(t => (t.id === saved.id ? saved : t)));
      setRenamingId(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signOut();
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        // Deleted from another tab while the input was open.
        setTemplates(ts => ts.filter(t => t.id !== renamingId));
        setRenamingId(null);
        setTemplatesError("That template no longer exists — it may have been deleted.");
        return;
      }
      setTemplatesError(templateWriteErrorMessage(err));
    } finally {
      setIsRenaming(false);
    }
  }

  if (!user) return null;

  const isOwner = user.role === "owner";

  return (
    <main id="main-content" className={ui.main}>
      <div className={ui.pageHeader}>
        <div>
          <h1>Templates</h1>
          <p className={ui.pageHint}>
            Reusable starting designs homeowners see on your public embed. Up to 20.
          </p>
        </div>
        {isOwner && (
          <Link to="/templates/new" className={ui.button}>
            + New template
          </Link>
        )}
      </div>

      {createdTemplateName && (
        <StatusMessage
          variant="status"
          message={`Saved “${createdTemplateName}” as a template.`}
          className={ui.status}
        />
      )}

      {conflicts.size > 0 && (
        <StatusMessage
          variant="status"
          message={conflicts.size === 1
            ? "One template uses a tool or option homeowners can’t choose. A homeowner who starts from it will be stopped at submit."
            : `${conflicts.size} templates use a tool or option homeowners can’t choose. A homeowner who starts from one will be stopped at submit.`}
          className={ui.warning}
        >
          {" "}{isOwner ? <>Fix the template, or change what the <Link to="/embed">Embed</Link> offers.</> : "Only the account owner can change this."}
        </StatusMessage>
      )}

      <section className={`${ui.card} ${styles.listCard}`} aria-labelledby="templates-heading">
        <h2 id="templates-heading" className="sr-only">Your templates</h2>
        {templatesLoading ? (
          <p className={ui.loading}>Loading templates…</p>
        ) : templates.length === 0 ? (
          <p className={ui.empty}>
            No templates yet.{isOwner ? " Create one to give homeowners a starting point." : ""}
          </p>
        ) : (
          <ul className={styles.templateList}>
            {templates.map((template) => (
              <li key={template.id} className={styles.templateRow}>
                {renamingId === template.id ? (
                  // Edit-in-place rename. Its own <form> so Enter submits;
                  // cancel is an explicit button.
                  <form onSubmit={handleRenameSubmit} className={styles.templateRenameForm}>
                    <input
                      type="text"
                      value={renameDraft}
                      onChange={e => setRenameDraft(e.target.value)}
                      maxLength={100}
                      aria-label={`New name for template ${template.name}`}
                      autoFocus
                    />
                    <button type="submit" className={`${ui.button} ${ui.buttonSmall}`} disabled={isRenaming}>
                      {isRenaming ? "Saving…" : "Save name"}
                    </button>
                    <button
                      type="button"
                      className={`${ui.buttonSecondary} ${ui.buttonSmall}`}
                      onClick={() => setRenamingId(null)}
                      disabled={isRenaming}
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <div className={styles.templateRowMain}>
                      <span className={styles.templateName}>{template.name}</span>
                      <span className={styles.templateDate}>
                        Saved {formatTemplateDate(template.createdAt)}
                      </span>
                      {conflicts.has(template.id) && (
                        <span className={`${ui.badge} ${ui.badgeWarn} ${styles.conflictBadge}`}>
                          {conflicts.get(template.id)}
                        </span>
                      )}
                    </div>
                    {isOwner && (
                      <div className={styles.templateRowActions}>
                        <Link
                          to={`/templates/${encodeURIComponent(template.id)}/edit`}
                          className={`${ui.buttonSecondary} ${ui.buttonSmall}`}
                          aria-label={`Edit design of template ${template.name}`}
                        >
                          Edit design
                        </Link>
                        <button
                          type="button"
                          className={`${ui.buttonSecondary} ${ui.buttonSmall}`}
                          onClick={() => { setRenamingId(template.id); setRenameDraft(template.name); }}
                          aria-label={`Rename template ${template.name}`}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className={`${ui.buttonDanger} ${ui.buttonSmall}`}
                          onClick={() => handleDeleteTemplate(template.id)}
                          disabled={deletingTemplateId !== null}
                          aria-label={`Delete template ${template.name}`}
                        >
                          {deletingTemplateId === template.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {templatesError && (
          <StatusMessage variant="error" message={templatesError} className={`${ui.error} ${styles.listError}`} />
        )}
      </section>

      <p className={`${ui.callout} ${styles.callout}`}>
        Editing a template changes what <strong>new</strong> homeowners start from.
        Designs they already created from it are their own copies and don&rsquo;t
        change. Deleting a template only removes it from this list; it doesn&rsquo;t
        change any lead that used it.
        {!isOwner && " Only the account owner can create, rename or delete templates."}
      </p>
    </main>
  );
}

function formatTemplateDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "at an unknown date";
  return d.toLocaleDateString("en-US", { dateStyle: "medium" });
}

// Maps the backend's documented error codes for DELETE
// /api/portal/templates/{templateId} to friendly copy (404 is handled
// separately, as a silent success, at the call site above). Falls back to
// the server's own message for anything not in this list.
function deleteTemplateErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code === "FORBIDDEN") {
    return "Only the account owner can delete templates.";
  }
  return err instanceof Error ? err.message : "Failed to delete template";
}
